import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Save, MessageSquare, Trash2, FileText, Package, ShieldCheck, Settings2 } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { Button } from '../components/ui/Button';
import { Tabs, type TabDef } from '../components/ui/Tabs';
import { Badge } from '../components/ui/Badge';
import { ConfirmDialog } from '../components/ui/Modal';
import { Skeleton } from '../components/ui/States';
import { agentsService } from '../services/agentsService';
import { agentFilesService } from '../services/agentFilesService';
import { artifactsService } from '../services/artifactsService';
import { toast } from '../store/uiStore';
import type { Agent, KnowledgeBase } from '../types';
import { DefinitionTab } from '../features/agents/DefinitionTab';
import { FilesTab } from '../features/agents/FilesTab';
import { ArtifactsTab } from '../features/agents/ArtifactsTab';
import { GuardrailsTab } from '../features/agents/GuardrailsTab';
import { countActiveGuardrails } from '../features/agents/guardrailsUtils';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function AgentEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAgentStudioNavigate();
  const { has } = useAgentStudioPermissions();
  const canDelete = has(P.agentsDelete);
  const canChat = has(P.agentsChat);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('definition');
  const [artifactCount, setArtifactCount] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([agentsService.get(id), agentsService.listKnowledgeBases(), agentFilesService.list(id).catch(() => null)]).then(([a, k, files]) => {
      // Espelha a lista real de arquivos (tabela agent_files) no agente, para o badge ficar correto já na abertura.
      setAgent(a ? (files ? { ...a, files } : a) : null);
      setKbs(k);
      setLoading(false);
    });
    artifactsService.listByAgent(id).then((a) => setArtifactCount(a.length)).catch(() => setArtifactCount(0));
  }, [id]);

  const patch = useCallback((partial: Partial<Agent>) => {
    setAgent((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  async function save() {
    if (!agent) return;
    setSaving(true);
    const saved = await agentsService.save(agent);
    setAgent(saved);
    setSaving(false);
    toast.success('Agente salvo.');
  }

  // Abre o chat (página dedicada). Salva antes, silenciosamente, para o backend
  // usar a configuração mais recente do agente (persona, modelo, guardrails…).
  async function goToChat() {
    if (agent) {
      try { await agentsService.save(agent); } catch { /* usa a última versão salva */ }
      navigate(`/agents/${agent.id}/chat`);
    }
  }

  async function remove() {
    if (!agent) return;
    await agentsService.remove(agent.id);
    toast.success('Agente excluído.');
    navigate('/agents');
  }

  if (loading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-96 w-full" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <p className="text-slate">Agente não encontrado.</p>
        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/agents')}>
          Voltar
        </Button>
      </div>
    );
  }

  const tabs: TabDef[] = [
    { id: 'definition', label: 'Definição', icon: Settings2 },
    { id: 'files', label: 'Arquivos', icon: FileText, badge: agent.files.length || undefined },
    { id: 'artifacts', label: 'Artefatos', icon: Package, badge: artifactCount || undefined },
    { id: 'guardrails', label: 'Guardrails & Controle', icon: ShieldCheck, badge: countActiveGuardrails(agent.guardrails) },
  ].filter((item) => item.id !== 'artifacts' || canChat);

  return (
    <>
      <div className="flex items-center justify-between gap-4 border-b border-line bg-white px-8 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" icon={ArrowLeft} onClick={() => navigate('/agents')} aria-label="Voltar" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-ink">{agent.name}</h1>
              <Badge tone="info"><ShieldCheck className="h-3 w-3" /> Guardrails: {countActiveGuardrails(agent.guardrails)} ativos</Badge>
            </div>
            <p className="text-xs text-slate">{agent.description || 'Sem descrição'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && <Button variant="ghost" icon={Trash2} onClick={() => setConfirmDelete(true)}>Excluir</Button>}
          {canChat && <Button variant="secondary" icon={MessageSquare} onClick={goToChat}>Conversar</Button>}
          <Button variant="primary" icon={Save} loading={saving} onClick={save}>Salvar</Button>
        </div>
      </div>

      <div className="border-b border-line bg-white px-8">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin bg-cloud p-8">
        <div className="mx-auto max-w-3xl">
          {tab === 'definition' && <DefinitionTab agent={agent} patch={patch} kbs={kbs} />}
          {tab === 'files' && <FilesTab agent={agent} patch={patch} />}
          {tab === 'artifacts' && <ArtifactsTab agent={agent} onCountChange={setArtifactCount} />}
          {tab === 'guardrails' && <GuardrailsTab agent={agent} patch={patch} />}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title="Excluir agente"
        message={`Tem certeza que deseja excluir "${agent.name}"?`}
      />
    </>
  );
}
